import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAppStore from '../store/useAppStore';
import { questions as allQuestions } from '../data/questions';
import { UserRole } from '../types';
import ComboEffect from '../components/ComboEffect';
import styles from './Quiz.module.css';

const Quiz: React.FC = () => {
  const navigate = useNavigate();
  const { levelId: paramLevelId } = useParams<{ levelId: string }>();
  const LEVEL_ID = paramLevelId || 'newborn-feeding-1';
  const userRole = useAppStore((s) => s.userRole);
  const addCompletedLevel = useAppStore((s) => s.addCompletedLevel);
  const addWrongQuestions = useAppStore((s) => s.addWrongQuestions);

  // Fisher-Yates 洗牌
  const shuffle = <T,>(arr: T[]): T[] => {
    const result = [...arr];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  };

  const demoQuestions = useMemo(() => {
    // 先按关卡过滤，再按用户角色过滤
    const levelQuestions = allQuestions.filter((q) => {
      if (q.levelId !== LEVEL_ID) return false;
      // 角色过滤：无 roles 则所有人可见，有 roles 则检查是否包含当前用户角色
      if (!q.roles || q.roles.length === 0) return true;
      return q.roles.includes(userRole as UserRole);
    });
    return shuffle(levelQuestions).slice(0, 5);
  }, [LEVEL_ID, userRole]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [showExplanation, setShowExplanation] = useState(false);

  // ===== Combo + 动效状态 =====
  const [comboCount, setComboCount] = useState(0);
  const [answerFlash, setAnswerFlash] = useState<'correct' | 'wrong' | null>(null);
  const [slideDir, setSlideDir] = useState<'in' | null>(null);
  const [showCombo, setShowCombo] = useState(false);
  const [comboAt, setComboAt] = useState(0);

  const currentQ = demoQuestions[currentIndex];
  const isLast = currentIndex === demoQuestions.length - 1;
  const isAnswered = !!answers[currentQ?.id];

  // 题目切换的滑动入场
  const triggerSlideIn = useCallback(() => {
    setSlideDir('in');
    setTimeout(() => setSlideDir(null), 400);
  }, []);

  // 首次渲染滑动入场
  useState(() => {
    triggerSlideIn();
  });

  const handleAnswerEffect = (correct: boolean) => {
    if (correct) {
      const newCombo = comboCount + 1;
      setComboCount(newCombo);
      setAnswerFlash('correct');
      if (newCombo >= 3) {
        setComboAt(newCombo);
        setShowCombo(true);
      }
    } else {
      setComboCount(0);
      setAnswerFlash('wrong');
    }
    setTimeout(() => setAnswerFlash(null), 600);
  };

  const handleSingleSelect = (option: string) => {
    const isCorrect = option === currentQ.correctAnswer;
    setAnswers({ ...answers, [currentQ.id]: option });
    setSelectedOptions([option]);
    setShowExplanation(false);
    handleAnswerEffect(isCorrect);
  };

  const handleMultiToggle = (option: string) => {
    const current = selectedOptions.includes(option)
      ? selectedOptions.filter((o) => o !== option)
      : [...selectedOptions, option];
    setSelectedOptions(current);
  };

  const handleMultiConfirm = () => {
    if (selectedOptions.length === 0) return;
    setAnswers({ ...answers, [currentQ.id]: selectedOptions });
    setShowExplanation(false);
    const correctAnswer = currentQ.correctAnswer as string[];
    const isCorrect =
      correctAnswer.length === selectedOptions.length &&
      correctAnswer.every((a) => selectedOptions.includes(a));
    handleAnswerEffect(isCorrect);
  };

  const handleTrueFalse = (option: string) => {
    const isCorrect = option === currentQ.correctAnswer;
    setAnswers({ ...answers, [currentQ.id]: option });
    setSelectedOptions([option]);
    setShowExplanation(false);
    handleAnswerEffect(isCorrect);
  };

  const checkCorrect = (qId: string) => {
    const userAnswer = answers[qId];
    const q = demoQuestions.find((x) => x.id === qId);
    if (!q) return false;
    const correctAnswer = q.correctAnswer;
    if (Array.isArray(correctAnswer) && Array.isArray(userAnswer)) {
      return (
        correctAnswer.length === userAnswer.length &&
        correctAnswer.every((a) => userAnswer.includes(a))
      );
    }
    return userAnswer === correctAnswer;
  };

  const handleFinish = () => {
    const total = demoQuestions.length;
    let correctCount = 0;
    const wrongIds: string[] = [];

    demoQuestions.forEach((q) => {
      if (checkCorrect(q.id)) {
        correctCount++;
      } else {
        wrongIds.push(q.id);
      }
    });

    addCompletedLevel({
      levelId: LEVEL_ID,
      completed: true,
      score: correctCount,
      correctCount,
      totalCount: total,
    });

    if (wrongIds.length > 0) {
      addWrongQuestions(
        wrongIds.map((id) => ({
          questionId: id,
          levelId: LEVEL_ID,
        }))
      );
    }

    navigate('/result', {
      state: { score: correctCount, total, levelId: LEVEL_ID },
    });
  };

  const handleNext = () => {
    if (isLast) {
      handleFinish();
    } else {
      setCurrentIndex(currentIndex + 1);
      setSelectedOptions([]);
      setShowExplanation(false);
      triggerSlideIn();
    }
  };

  if (demoQuestions.length === 0 || !currentQ) {
    return (
      <div className={styles.container}>
        <div className={styles.questionCard}>
          <h2 className={styles.questionText}>该关卡题目正在准备中</h2>
          <p style={{ color: '#8b6f5c', marginBottom: '16px' }}>敬请期待更多题目上线</p>
          <button className={styles.submitBtn} onClick={() => navigate('/map')}>返回学习地图</button>
        </div>
      </div>
    );
  }

  const progressPercent = Math.round(((currentIndex + 1) / demoQuestions.length) * 100);

  const cardFlashClass =
    answerFlash === 'correct' ? styles.cardFlashCorrect : answerFlash === 'wrong' ? styles.cardFlashWrong : '';

  const slideClass = slideDir === 'in' ? styles.cardSlideIn : '';

  return (
    <div className={styles.container}>
      {/* Combo 特效 */}
      <ComboEffect
        combo={comboAt}
        visible={showCombo}
        onDone={() => setShowCombo(false)}
      />

      <div className={styles.topBar}>
        <button className={styles.homeLink} onClick={() => navigate(-1)}>
          ← 返回
        </button>
        {/* 连续答对指示 */}
        {comboCount >= 2 && (
          <span className={styles.comboIndicator}>
            🔥 {comboCount}连对
          </span>
        )}
      </div>
      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
      </div>
      <div className={styles.progressText}>
        题目 {currentIndex + 1} / {demoQuestions.length}
      </div>

      <div className={`${styles.questionCard} ${cardFlashClass} ${slideClass}`} key={currentIndex}>
        <div className={styles.questionType}>
          {currentQ.type === 'single' && '单选题'}
          {currentQ.type === 'multiple' && '多选题'}
          {currentQ.type === 'truefalse' && '判断题'}
          {currentQ.type === 'scenario' && '情景题'}
        </div>

        {currentQ.type === 'scenario' && (
          <div className={styles.scenarioBanner}>📋 情景模拟：请根据以下场景做出判断</div>
        )}

        <h2 className={styles.questionText}>{currentQ.text}</h2>

        <div className={styles.options}>
          {currentQ.options?.map((option, idx) => {
            if (currentQ.type === 'multiple') {
              const isSelected = selectedOptions.includes(option);
              return (
                <button
                  key={idx}
                  className={`${styles.optionBtn} ${isSelected ? styles.optionSelected : ''}`}
                  onClick={() => handleMultiToggle(option)}
                  disabled={isAnswered}
                >
                  <span className={styles.optionCheck}>{isSelected ? '☑' : '☐'}</span>
                  {option}
                </button>
              );
            }
            return (
              <button
                key={idx}
                className={`${styles.optionBtn} ${
                  selectedOptions.includes(option) ? styles.optionSelected : ''
                }`}
                onClick={() =>
                  currentQ.type === 'truefalse'
                    ? handleTrueFalse(option)
                    : handleSingleSelect(option)
                }
                disabled={isAnswered}
              >
                {String.fromCharCode(65 + idx)}. {option}
              </button>
            );
          })}
        </div>

        {currentQ.type === 'multiple' && !isAnswered && selectedOptions.length > 0 && (
          <button className={styles.confirmBtn} onClick={handleMultiConfirm}>
            确认选择
          </button>
        )}

        {isAnswered && !showExplanation && (
          <div className={`${styles.feedback} ${checkCorrect(currentQ.id) ? styles.correct : styles.wrong}`}>
            <span className={styles.feedbackIcon}>
              {checkCorrect(currentQ.id) ? '✓' : '✗'}
            </span>
            <span>{checkCorrect(currentQ.id) ? '回答正确！' : '回答错误'}</span>
            <button className={styles.showExplainBtn} onClick={() => setShowExplanation(true)}>
              查看解释
            </button>
          </div>
        )}

        {showExplanation && (
          <div className={styles.explanation}>
            <h3>📖 解释</h3>
            <p>{currentQ.explanation}</p>
          </div>
        )}

        {isAnswered && (
          <button className={styles.submitBtn} onClick={handleNext}>
            {isLast ? '查看结果' : '下一题'}
          </button>
        )}
      </div>
    </div>
  );
};

export default Quiz;